<template>
  <div class="cms-team-members">
    <cms-page-header
      :title="$t('cms.team.title')"
      :create-text="$t('cms.team.createNew')"
      @create="navigateToCreate"
    />

    <cms-filter-bar>
      <cms-filter-group :label="$t('cms.team.filters.language')">
        <select v-model="selectedLanguage" class="cms-select">
          <option v-for="language in languageOptions" :key="language.value" :value="language.value">
            {{ language.label }}
          </option>
        </select>
      </cms-filter-group>

      <cms-filter-group :label="$t('general.search')">
        <input
          v-model="searchQuery"
          type="text"
          class="cms-input"
          :placeholder="$t('cms.team.searchPlaceholder')"
        />
      </cms-filter-group>

      <cms-filter-group :label="''">
        <button class="cms-btn cms-btn-secondary" type="button" :disabled="isLoading" @click="loadMembers">
          <v-icon icon="refresh" size="sm" />
          <span>{{ $t('general.refresh') }}</span>
        </button>
      </cms-filter-group>
    </cms-filter-bar>

    <div v-if="errorMessage" class="cms-team-members-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="cms-btn-link" @click="loadMembers">{{ $t('general.retry') }}</button>
    </div>

    <div v-if="isLoading" class="cms-team-members-loading">
      <div class="cms-loading-spinner" />
      <p>{{ $t('general.loading') }}</p>
    </div>

    <cms-empty-state
      v-else-if="members.length === 0"
      :message="$t('cms.team.list.emptyStateTitle')"
      :hint="$t('cms.team.list.emptyStateHint')"
    />

    <cms-grid v-else min-width="360px">
      <cms-card
        v-for="member in members"
        :key="member.id"
        clickable
        class="cms-team-card"
        @click="navigateToEdit(member.id)"
      >
        <template #status>
          <cms-status-badge :status="member.status">
            {{ member.status }}
          </cms-status-badge>
        </template>

        <template #actions>
          <cms-icon-button
            :icon="member.isPublic ? 'eye-blocked' : 'eye'"
            :variant="member.isPublic ? 'secondary' : 'primary'"
            :disabled="publishingId === member.id"
            :tooltip="member.isPublic ? $t('cms.team.unpublishSuccess') : $t('cms.team.publishSuccess')"
            @click.stop="togglePublish(member)"
          />
          <cms-icon-button
            icon="pencil"
            variant="primary"
            :tooltip="$t('general.edit')"
            @click.stop="navigateToEdit(member.id)"
          />
          <cms-icon-button
            icon="trash"
            variant="danger"
            :disabled="deletingId === member.id"
            :tooltip="$t('general.delete')"
            @click.stop="deleteMember(member)"
          />
        </template>

        <div
          v-if="member.featuredImage"
          class="cms-team-preview"
          :style="{ backgroundImage: `url(${member.featuredImage})` }"
        />
        <div v-else class="cms-team-preview-placeholder">
          <v-icon icon="user" size="lg" />
        </div>

        <div class="cms-team-header">
          <h3 class="cms-team-title">{{ member.name }}</h3>
        </div>

        <p v-if="member.role" class="cms-team-role">{{ member.role }}</p>

        <template #footer>
          <div class="cms-card-footer">
            <span class="cms-team-order">{{ $t('cms.team.order') }}: {{ member.order }}</span>
          </div>
        </template>
      </cms-card>
    </cms-grid>

    <cms-pagination
      v-if="paginationMeta"
      :meta="paginationMeta"
      :per-page-options="[9, 18, 27, 36]"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import {
  CmsPageHeader,
  CmsFilterBar,
  CmsFilterGroup,
  CmsGrid,
  CmsCard,
  CmsStatusBadge,
  CmsIconButton,
  CmsEmptyState,
  CmsPagination,
} from '@/components/cms';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource, extractRelationship } from '@/composables/useCmsActivityData';

interface CmsTeamCard {
  id: number;
  name: string;
  role: string;
  order: number;
  featuredImage?: string;
  isPublic: boolean;
  status: 'draft' | 'published';
}

export default defineComponent({
  name: 'TheCmsTeamMembers',
  components: {
    CmsPageHeader,
    CmsFilterBar,
    CmsFilterGroup,
    CmsGrid,
    CmsCard,
    CmsStatusBadge,
    CmsIconButton,
    CmsEmptyState,
    CmsPagination,
    VIcon,
  },
  setup() {
    const router = useRouter();
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { locales, resolveStoredLocale } = useAppLanguages();
    const selectedLanguage = ref(resolveStoredLocale(localStorage.getItem('language')));

    const members = ref<CmsTeamCard[]>([]);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const deletingId = ref<number | null>(null);
    const publishingId = ref<number | null>(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const perPage = ref(9);
    const paginationMeta = ref<{
      currentPage: number;
      lastPage: number;
      perPage: number;
      from: number | null;
      to: number | null;
      total: number;
    } | null>(null);

    const languageOptions = computed(() =>
      locales.value.map((locale) => ({
        value: locale,
        label: locale.toUpperCase(),
      })),
    );

    const extractListData = (response: any): any[] => {
      if (!response) return [];
      const candidates = [response?.data?.data, response?.data, response?.data?.data?.data, response];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    };

    const extractMediaItems = (record: any) => {
      const relationship = extractRelationship(record, 'media');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) return normalized;
      if (Array.isArray(record?.media)) return record.media;
      return [];
    };

    const extractTranslations = (record: any) => {
      const relationship = extractRelationship(record, 'translations');
      const normalized = normalizeResource(relationship);
      if (Array.isArray(normalized)) return normalized;
      if (Array.isArray(record?.translations)) return record.translations;
      if (normalized) return [normalized];
      return [];
    };

    const mapMemberRecord = (item: any): CmsTeamCard => {
      const normalized = normalizeResource(item) || item;
      const translations = extractTranslations(normalized);
      const translation =
        translations.find((tr: any) => (tr?.locale || '').toLowerCase() === selectedLanguage.value) ||
        translations[0] ||
        {};
      const mediaItems = extractMediaItems(normalized);
      return {
        id: normalized.id,
        name: translation?.name || normalized.name || '-',
        role: translation?.role || '',
        order: normalized.order ?? 0,
        featuredImage: mediaItems[0]?.originalUrl || '',
        isPublic: !!normalized.isPublic,
        status: normalized.isPublic ? 'published' : 'draft',
      };
    };

    const loadMembers = async () => {
      if (!agencySlug.value) return;

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const filters: Record<string, any> = {
          language: selectedLanguage.value,
        };
        if (searchQuery.value.trim()) {
          filters.search = searchQuery.value.trim();
        }

        const response = await api.teamMemberService.listCmsTeamMembers(agencySlug.value, {
          page: currentPage.value,
          perPage: perPage.value,
          filters,
          order: { key: 'order', direction: 'asc' },
        });

        const list = extractListData(response);
        members.value = list.map(mapMemberRecord);

        if (response?.meta) {
          paginationMeta.value = {
            currentPage: response.meta.current_page || currentPage.value,
            lastPage: response.meta.last_page || 1,
            perPage: response.meta.per_page || perPage.value,
            from: response.meta.from || null,
            to: response.meta.to || null,
            total: response.meta.total || 0,
          };
        } else {
          paginationMeta.value = null;
        }
      } catch (error: any) {
        console.error('Failed to load team members', error);
        errorMessage.value = error?.response?.data?.message || t('general.errorMessage');
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
      loadMembers();
    };

    const handlePerPageChange = (newPerPage: number) => {
      perPage.value = newPerPage;
      currentPage.value = 1;
      loadMembers();
    };

    watch(
      () => agencySlug.value,
      (slug) => {
        if (slug) {
          currentPage.value = 1;
          loadMembers();
        }
      },
      { immediate: true },
    );

    watch(selectedLanguage, () => {
      if (!agencySlug.value) return;
      currentPage.value = 1;
      loadMembers();
    });

    let searchDebounce: ReturnType<typeof setTimeout> | null = null;
    watch(
      () => searchQuery.value,
      () => {
        if (!agencySlug.value) return;
        if (searchDebounce) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
          currentPage.value = 1;
          loadMembers();
        }, 400);
      },
    );

    const navigateToCreate = () => router.push({ name: 'cms.team-members.create' });
    const navigateToEdit = (id: number) => router.push({ name: 'cms.team-members.edit', params: { id } });

    const togglePublish = async (member: CmsTeamCard) => {
      if (!agencySlug.value) return;
      publishingId.value = member.id;
      try {
        await api.teamMemberService.publishCmsTeamMember(agencySlug.value, member.id, {
          is_public: !member.isPublic,
        });
        const index = members.value.findIndex((m) => m.id === member.id);
        if (index !== -1) {
          members.value[index].isPublic = !member.isPublic;
          members.value[index].status = members.value[index].isPublic ? 'published' : 'draft';
        }
        toast.success(member.isPublic ? t('cms.team.unpublishSuccess') : t('cms.team.publishSuccess'));
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        publishingId.value = null;
      }
    };

    const deleteMember = async (member: CmsTeamCard) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) return;

      deletingId.value = member.id;
      try {
        await api.teamMemberService.deleteCmsTeamMember(agencySlug.value, member.id);
        members.value = members.value.filter((item) => item.id !== member.id);
        toast.success(t('cms.team.deleteSuccess'));
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        deletingId.value = null;
      }
    };

    return {
      members,
      selectedLanguage,
      searchQuery,
      languageOptions,
      isLoading,
      errorMessage,
      deletingId,
      publishingId,
      paginationMeta,
      loadMembers,
      navigateToCreate,
      navigateToEdit,
      togglePublish,
      deleteMember,
      handlePageChange,
      handlePerPageChange,
    };
  },
});
</script>

<style lang="scss" scoped>
.cms-team-members-error {
  margin: 1rem 0;
  color: #b91c1c;
}

.cms-team-members-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
}

.cms-team-preview,
.cms-team-preview-placeholder {
  height: 160px;
  border-radius: 8px;
  margin-bottom: 1rem;
  background-size: cover;
  background-position: center;
}

.cms-team-preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
}

.cms-team-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.cms-team-role {
  margin: 0.5rem 0 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.cms-team-order {
  font-size: 0.85rem;
  color: #6b7280;
}
</style>
